import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { instagramPath, facebookPath, yelpPath } from '../common/socialIconPaths';
import { dishes } from '../../data/dishes';

const Footer = () => {
  return (
    <footer className="bg-secondary text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo and tagline */}
          <div className="col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src="/assets/images/optimized/ThaprachanLogo.webp" alt="Thaprachan Thai" className="h-16" />
            </Link>
            <p className="text-sm opacity-80">
              Authentic Thai cuisine with a modern twist, served in a warm and welcoming atmosphere.
            </p>
          </div>

          {/* Contact Info */}
          <div className="col-span-1">
            <h3 className="font-heading text-xl mb-4 text-primary-light">Contact Us</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <a href="tel:+12065233938" data-track="call_click" className="inline-block py-2.5 leading-6 hover:text-primary-light transition-colors">
                  (206) 523-3938
                </a>
              </li>
              <li>
                <a href="https://thaprachanthaicuisine.com" target="_blank" rel="noopener noreferrer" className="inline-block py-2.5 leading-6 hover:text-primary-light transition-colors">
                  thaprachanthaicuisine.com
                </a>
              </li>
              <li>
                <p>805 NE 65th St<br />Seattle, WA 98115</p>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div className="col-span-1">
            <h3 className="font-heading text-xl mb-4 text-primary-light">Hours</h3>
            <ul className="space-y-2 text-sm">
              <li>Monday: 11am-3pm, 4pm-9pm</li>
              <li>Tuesday: 11am-3pm, 4pm-9pm</li>
              <li>Wednesday: 11am-3pm, 4pm-9pm</li>
              <li>Thursday: 11am-3pm, 4pm-10pm</li>
              <li>Friday: 11am-10pm</li>
              <li>Saturday: 11am-10pm</li>
              <li>Sunday: 12pm-9pm</li>
            </ul>
          </div>

          {/* Signature dishes — descriptive internal links, one per dish page */}
          <div className="col-span-1">
            <h3 className="font-heading text-xl mb-4 text-primary-light">Signature Dishes</h3>
            <ul className="space-y-1 text-sm">
              {dishes.map((dish) => (
                <li key={dish.slug}>
                  <Link
                    to={`/dishes/${dish.slug}`}
                    className="inline-block py-2 leading-6 hover:text-primary-light transition-colors"
                  >
                    {dish.name} in Seattle
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/thai-street-food-seattle" className="inline-block py-2 leading-6 hover:text-primary-light transition-colors">
                  Thai street food
                </Link>
              </li>
              <li>
                <Link to="/vegetarian-thai-seattle" className="inline-block py-2 leading-6 hover:text-primary-light transition-colors">
                  Vegetarian Thai food
                </Link>
              </li>
              <li>
                <Link to="/menu" className="inline-block py-2 leading-6 hover:text-primary-light transition-colors">
                  Full menu
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Links */}
          <div className="col-span-1">
            <h3 className="font-heading text-xl mb-4 text-primary-light">Follow Us</h3>
            <div className="flex space-x-4">
              <SocialLink href="https://www.instagram.com/thaprachanthai/" label="Instagram" path={instagramPath} />
              <SocialLink href="https://www.facebook.com/thaprachanthaiofficials/" label="Facebook" path={facebookPath} />
              <SocialLink href="https://www.yelp.com/biz/thaprachan-thai-cuisine-seattle-5" label="Yelp" path={yelpPath} />
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-center text-xs opacity-85">
          <p>© {new Date().getFullYear()} Thaprachan Thai. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const SocialLink = ({ href, label, path }: { href: string; label: string; path: string }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary transition-colors"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      aria-label={label}
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
        <path d={path} />
      </svg>
    </motion.a>
  );
};

export default Footer;
